import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchConfig } from "../lib/api";
import LeadWizard from "../components/wizard/LeadWizard";
import {
  Sun,
  ShieldCheck,
  Hammer,
  Calendar,
  Sparkles,
  CheckCircle2,
  Phone,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/flagged/photo-1566838616631-f2618f74a6a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxob3VzZSUyMHdpdGglMjBzb2xhciUyMHBhbmVscyUyMHJvb2Z8ZW58MHx8fHwxNzgyNjczNDIyfDA&ixlib=rb-4.1.0&q=85";
const DFW_IMG =
  "https://images.unsplash.com/photo-1621904878414-d4ca4756bd7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxkYWxsYXMlMjBza3lsaW5lfGVufDB8fHx8MTc4MjY3MzQyMnww&ixlib=rb-4.1.0&q=85";
const ROOF_IMG =
  "https://images.unsplash.com/photo-1655300283246-1ef0317a565d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85&auto=format&fit=crop&ixlib=rb-4.1.0";

export default function Landing() {
  const [cfg, setCfg] = useState({ business_name: "Cash Bid Roofing", business_phone: "682-715-7445", business_phone_local: "", service_area: "Fort Worth / Dallas, TX", tdlr_license: "TECL 37838", electrical_partner: "Excel Construction Group" });
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    fetchConfig().then(setCfg).catch(() => {});
  }, []);

  const openWizard = () => setWizardOpen(true);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--fg)" }}>
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-display text-xl font-semibold tracking-tight block leading-none">{cfg.business_name}</span>
              <span className="text-[10px] text-slate-500 font-mono tracking-wider" data-testid="header-tdlr-license">{cfg.tdlr_license}</span>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#why" className="hover:text-slate-900">Solar specialty</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
            <a href={`tel:+1${cfg.business_phone.replace(/-/g, '')}`} className="flex items-center gap-1.5 hover:text-slate-900" data-testid="header-phone">
              <Phone className="w-4 h-4" /> {cfg.business_phone}
            </a>
          </nav>
          <button className="btn-amber text-sm" onClick={openWizard} data-testid="header-cta-button">
            Get Instant Bid
          </button>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold tracking-wider uppercase mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI-Powered Roofing for DFW
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tighter text-slate-900">
              Your roof bid,
              <br />
              <span className="text-amber-600">in 90 seconds.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Skip the four salespeople and the hard pitch. Tell us about your home, upload a few photos, and get a real instant bid. Your DFW specialists for roofing, gutters, solar, and storm restoration.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button className="btn-amber text-base" onClick={openWizard} data-testid="hero-cta-button">
                Get my instant bid <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
              <a href={`tel:+1${cfg.business_phone.replace(/-/g, '')}`} className="text-slate-700 font-medium hover:text-slate-900" data-testid="hero-call-link">
                or call {cfg.business_phone}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="<2 min" l="Bid time" />
              <Stat n="DFW" l="Local crews" />
              <Stat n="TDLR" l="Licensed" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl fade-up-d2">
              <img src={HERO_IMG} alt="Home with solar panels" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300 font-semibold">
                  <Sun className="w-4 h-4" /> Roofing · Gutters · Solar · Storm
                </div>
                <p className="font-display text-2xl mt-2 leading-tight">Got solar panels and no one to call when something breaks? We pick up the phone.</p>
              </div>
            </div>
            {/* Floating card */}
            <div className="hidden md:block absolute -left-10 bottom-10 bg-white border border-slate-200 rounded-xl p-4 shadow-xl w-64 fade-up-d3">
              <div className="text-xs font-semibold text-amber-600 uppercase tracking-widest">Sample bid</div>
              <div className="font-display text-2xl font-semibold mt-1">$10,100 – $13,100</div>
              <div className="text-xs text-slate-500 mt-1">1,950 sqft asphalt re-roof</div>
              <div className="tracing mt-3" />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-3">How it works</div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
                Three steps. Zero sales pressure.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We replaced the four-visit sales funnel with a 90-second form and a real inspection. The bid you see online is the bid we honor on-site (within tolerance).
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
              <Step n="01" title="Tell us about your roof" body="Address, roof pitch, shingle type, and size — plus solar or gutters if that's what you need." icon={<Hammer className="w-5 h-5" />} />
              <Step n="02" title="Get your AI bid" body="A real price range in seconds, calibrated to DFW labor and material costs." icon={<Sparkles className="w-5 h-5" />} />
              <Step n="03" title="Schedule inspection" body="Pick a slot. We confirm the bid on-site, then it's straight to production." icon={<Calendar className="w-5 h-5" />} />
            </div>
          </div>
        </div>
      </section>

      {/* Why solar specialty */}
      <section id="why" className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative order-2 lg:order-1">
            <img src={ROOF_IMG} alt="Roof tiles" className="rounded-2xl w-full h-[440px] object-cover" />
            <div className="absolute -right-6 -bottom-6 bg-slate-900 text-white p-6 rounded-xl w-64 shadow-2xl">
              <Sun className="w-6 h-6 text-amber-400" />
              <div className="font-display text-xl mt-3 leading-tight">Tens of thousands of DFW homeowners have nowhere to call for solar repairs. We do.</div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-3">Solar Repair Specialty</div>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
              Solar installed. Installer ghosted. Now what?
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Tens of thousands of Texas homeowners have working (or sort-of working) rooftop solar with no one to call when a panel fails, wiring chews up, or an inverter throws errors. We&apos;re a TDLR-licensed electrical contractor ({cfg.tdlr_license}) — fulfilled by {cfg.electrical_partner} — that takes those calls.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              <strong>For a full re-roof on a solar home,</strong> we detach and reset your panels as part of the project — one licensed team, one point of contact, no juggling a separate solar company.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Solar panel & inverter repair (panel reseat, rewire, replacement)",
                "Full re-roof with in-house solar detach & reset",
                "TDLR electrical license + permitted, inspected work",
                "DFW pilot: Fort Worth, Dallas, Arlington, Plano, McKinney",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <button className="btn-primary mt-8" onClick={openWizard} data-testid="why-cta-button">
              Get my bid
            </button>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-3 gap-8">
          <TrustCard icon={<ShieldCheck className="w-6 h-6" />} title="TDLR licensed" body={`Texas Department of Licensing & Regulation — Electrical Contractor ${cfg.tdlr_license}. Fully insured general liability and workers' comp on every job.`} />
          <TrustCard icon={<MapPin className="w-6 h-6" />} title="DFW production fulfillment" body={`Electrical and solar work fulfilled by ${cfg.electrical_partner} under our TDLR license. Roofing handled by our vetted, insured installation team.`} />
          <TrustCard icon={<Sparkles className="w-6 h-6" />} title="Transparent AI pricing" body="Same algorithm for every homeowner. Real DFW labor and material costs — no 'special discounts' that aren't actually special." />
        </div>
      </section>

      {/* Service lines */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-3">What we do</div>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight max-w-2xl">
            Four specialties. One phone number.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 lift-on-hover" data-testid="service-card-roofing">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Re-roof &amp; repair</div>
              <div className="font-display text-2xl font-semibold mt-2 tracking-tight">Roofing</div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Full tear-off re-roofs and targeted repairs — shingles, flashing, pipe boots, vents, ridge vents. Get a real instant bid online in under 2 minutes.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-8 lift-on-hover" data-testid="service-card-gutters">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Seamless</div>
              <div className="font-display text-2xl font-semibold mt-2 tracking-tight">Gutters</div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Seamless gutters and downspouts installed at $8.75 per linear foot. Know your footage? We&apos;ll bid it fast — two-story homes we measure on site.</p>
            </div>
            <div className="bg-white border border-amber-300 rounded-2xl p-8 lift-on-hover ring-1 ring-amber-200" data-testid="service-card-solar">
              <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Specialty</div>
              <div className="font-display text-2xl font-semibold mt-2 tracking-tight">Solar</div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Panel reseat, wiring, and inverter repair — plus in-house detach &amp; reset on solar-home re-roofs. TDLR-licensed electrical work, {cfg.tdlr_license}.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-8 lift-on-hover" data-testid="service-card-storm">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Wind &amp; hail</div>
              <div className="font-display text-2xl font-semibold mt-2 tracking-tight">Storm Restoration</div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Hit by a Texas hail or wind storm? Book a free damage inspection and we&apos;ll walk your roof, gutters, and solar to spot what needs restoring.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-12 py-24">
        <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-3">FAQ</div>
        <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mb-10">Questions homeowners actually ask.</h2>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-xl px-5 bg-white">
              <AccordionTrigger className="text-left font-medium" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA Footer */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter leading-tight">
              Skip the sales call. <span className="text-amber-400">Get your bid.</span>
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl">Serving {cfg.service_area}. Average homeowner finishes the bid in under 2 minutes.</p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            <button className="btn-amber" onClick={openWizard} data-testid="footer-cta-button">Get my instant bid</button>
            <a href={`tel:+1${cfg.business_phone.replace(/-/g, '')}`} className="px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition" data-testid="footer-call-link">
              <Phone className="inline w-4 h-4 mr-2" /> {cfg.business_phone}
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 grid md:grid-cols-3 gap-6 text-sm text-slate-400" data-testid="tdlr-compliance-footer">
            <div>
              <div className="font-display text-white text-base">{cfg.business_name}</div>
              <div className="mt-2 text-slate-400 text-xs leading-relaxed">
                TDLR-licensed contractor for roofing, gutters, solar &amp; storm damage restoration in {cfg.service_area}.
              </div>
              <div className="mt-3 text-xs text-amber-300 font-mono tracking-wider" data-testid="tdlr-license-display">{cfg.tdlr_license}</div>
            </div>
            <div className="text-xs leading-relaxed">
              <div className="text-white font-semibold mb-2">Production Fulfillment</div>
              <div>Electrical &amp; solar: {cfg.electrical_partner}</div>
              <div>Roofing: vetted DFW installation team</div>
            </div>
            <div className="text-xs leading-relaxed">
              <div className="text-white font-semibold mb-2">TDLR Complaint Information</div>
              <div className="text-slate-400">
                Regulated by the Texas Department of Licensing and Regulation, P.O. Box 12157, Austin, TX 78711. Phone <a href="tel:18008039202" className="text-slate-300 hover:text-white">1-800-803-9202</a>, web <a href="https://www.tdlr.texas.gov" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white">tdlr.texas.gov</a>.
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center justify-between text-xs text-slate-500 border-t border-white/5">
            <div>© {new Date().getFullYear()} {cfg.business_name}. All rights reserved.</div>
            <div className="flex items-center gap-6">
              <img src={DFW_IMG} alt="DFW" className="hidden md:block h-8 w-20 object-cover rounded opacity-70" />
              <Link to="/admin/login" className="hover:text-white" data-testid="admin-portal-link">Admin portal</Link>
            </div>
          </div>
        </div>
      </section>

      {wizardOpen && <LeadWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-2xl font-semibold text-slate-900">{n}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{l}</div>
    </div>
  );
}

function Step({ n, title, body, icon }) {
  return (
    <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 lift-on-hover">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-sm text-amber-600 font-semibold">{n}</span>
        <span className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-700">{icon}</span>
      </div>
      <h3 className="font-display text-xl font-medium tracking-tight">{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function TrustCard({ icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="font-display text-lg font-medium">{title}</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

const FAQS = [
  { q: "How accurate is the instant AI bid?", a: "Our AI uses real DFW labor and material costs to produce a range that's typically within 10–15% of the final inspection price. The on-site visit confirms decking condition, ventilation, and any solar reset specifics before we issue the contract." },
  { q: "What if I don't know my roof's square footage or age?", a: "No problem — pick the closest home-size range and we'll estimate your roofing squares from it. If you're unsure, we use a sensible default and lock the exact number during your on-site inspection. You're never penalized for not having the info." },
  { q: "Who actually does the work?", a: "Cash Bid Roofing is your general contractor and single point of contact. Roofing is performed by our vetted DFW installation team. All electrical work — including any solar panel disconnect, repair, or reconnect — is performed by Excel Construction Group under our TDLR Electrical Contractor license (TECL 37838). Both teams are fully insured." },
  { q: "Do you really handle solar panel removal?", a: "Yes. Because we're a TDLR-licensed electrical contractor, the panel disconnect and reset stays inside our project — one licensed team handles your roof and your solar from start to finish." },
  { q: "How much do gutters cost?", a: "Seamless gutters and downspouts run $8.75 per linear foot installed. If you know your total footage we can bid it quickly; on two-story homes we'll take a quick on-site measurement first." },
  { q: "My roof was hit by a storm — what do I do?", a: "Book a free storm-damage inspection. We'll walk your roof, gutters, and any solar for wind and hail damage, document what we find, and give you a clear plan and pricing for the restoration." },
  { q: "I have a broken solar panel but I don't need a new roof. Can you help?", a: "Yes — this is one of our specialties. Tens of thousands of DFW homeowners have working (or partially working) solar with no one to call when a panel, inverter, or wiring issue comes up. Submit a 'Solar Repair' request and we'll send a TDLR-licensed electrician for diagnosis." },
  { q: "When can you start?", a: "Most DFW roofs are scheduled within 7–14 days of the inspection. Storm-season turnaround may be longer, but we'll give you a real date — not 'in a few weeks.'" },
  { q: "How do I file a complaint with TDLR?", a: "You can file a complaint with the Texas Department of Licensing and Regulation against our electrical license (TECL 37838) at tdlr.texas.gov, by emailing customer.service@tdlr.texas.gov, or by calling 1-800-803-9202. We display this information per TDLR advertising rules." },
];
